import apiFetch from '@scale/frontend-lib/api-fetch';
import logger from '@scale/frontend-lib/logging';
import { MessageRole } from '@scale/shared/types/chat';
import {
  CreateCompletionRequest,
  CreateCompletionResponse,
  StrategyName,
} from '@scale/shared/types/chat-completion';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import useLocalStorageWithExpiry from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useLocalStorageWithExpiry';

const TEXT_LENGTH_THRESHOLD = 30;
const LANG_STREAK_THRESHOLD = 3;
const PLACEHOLDERS = ['und', 'zxx'];
const RTL_ISO_CODES = ['ara', 'fas', 'heb', 'urd', 'pus', 'snd', 'yid'];

function generateGPTQuery(text: string, projectId: string) {
  return async () => {
    const completionRequest: CreateCompletionRequest = {
      strategyName: StrategyName.OpenAIStrategy,
      params: {
        model: 'gpt-4o',
        max_tokens: 2048,
        stream: false,
      },
      messages: [
        {
          role: MessageRole.User,
          content: 'What language is the following text in?\n'
            .concat(text)
            .concat('\nReply only the ISO 639-3 language code. Do not include anything else.'),
        },
      ],
      projectId: projectId || null,
      useCase: 'auto-language-detection',
    };

    const data = await apiFetch<CreateCompletionResponse>(
      null,
      'internal/self_serve/rlhf/createCompletion',
      {
        method: 'POST',
        body: completionRequest,
      },
    );

    return data;
  };
}

const useAutoLanguageDetection = (
  enabled: boolean,
  text: string,
  queryKey: (string | undefined)[],
  taskId: string,
  projectID: string,
  baseLocalSaveKey: string,
) => {
  const [detectedLang, setDetectedLang] = useState<string | undefined>(); // state
  const [detectedLangStreak, setDetectedLangStreak] = useLocalStorageWithExpiry(
    1,
    `${baseLocalSaveKey}-lang-streak`,
    true,
  );
  const [langUndefined, setLangUndefined] = useLocalStorageWithExpiry(
    true,
    `${baseLocalSaveKey}-lang-undefined`,
    true,
  );

  const { isFetching, data, refetch } = useQuery(queryKey, generateGPTQuery(text, projectID), {
    enabled: false, // only fetch when refetch is called
    staleTime: 200,
  });

  useEffect(() => {
    if (
      enabled &&
      !isFetching &&
      text.length > 0 &&
      (text.length < TEXT_LENGTH_THRESHOLD ||
        detectedLangStreak < LANG_STREAK_THRESHOLD ||
        langUndefined)
    ) {
      logger.info(
        `Hitting language detection service for Project ID: ${projectID},  Task ID: ${taskId}`,
      );
      refetch();

      const newDetectedLang = data?.choices?.length ? data?.choices[0].content : undefined; // recalculate
      const streak = newDetectedLang === detectedLang ? detectedLangStreak + 1 : 1;

      setDetectedLangStreak(streak); // reset
      setDetectedLang(newDetectedLang);
      setLangUndefined(detectedLang?.length !== 3 || PLACEHOLDERS.includes(detectedLang));
    }
  }, [enabled, text]);

  return detectedLang;
};

export default useAutoLanguageDetection;

export const useRightToLeft = ({
  enabled,
  text,
  queryKey,
  baseLocalSaveKey,
  taskId,
  projectId,
}: {
  enabled: boolean;
  text: string;
  queryKey: (string | undefined)[];
  taskId: string;
  projectId: string;
  baseLocalSaveKey: string;
}) => {
  const detectedLang = useAutoLanguageDetection(
    enabled,
    text,
    queryKey,
    taskId,
    projectId,
    baseLocalSaveKey,
  );
  return RTL_ISO_CODES.includes((detectedLang ?? 'undefined').trim().toLowerCase());
};
